import {
	Box,
	Card,
	CardContent,
	Grid,
	Stack,
	Tooltip,
	Typography,
	TypographyProps,
} from '@mui/material';
import { colours } from '../../shared/colors';
import { useEffect, useRef, useState } from 'react';
import CyberLiabilityIcon from '../icons/CyberLiabilityIcon';
import ProductRecallCGCPIcon from '../icons/ProductRecallCGCPIcon';
import ProductRecallCGIcon from '../icons/ProductRecallCGIcon';
import ManagementLiabilityIcon from '../icons/ManagementLiabilityIcon';

const IconFromProductName = ({ productName }: { productName: string }) => {
	const iconProps = { sx: { width: '100%', height: 'auto' } };

	if (productName.includes('cyber')) {
		return <CyberLiabilityIcon {...iconProps} />;
	} else if (productName.includes('component')) {
		return <ProductRecallCGCPIcon {...iconProps} />;
	} else if (productName.includes('consumer') || productName.includes('consumable')) {
		return <ProductRecallCGIcon {...iconProps} />;
	} else {
		// Default to Management Liability Icon
		return <ManagementLiabilityIcon {...iconProps} />;
	}
};

const LINES = 4;

/**
 * ProductCard component for displaying product information in a card.
 *
 * @param {object} iconProps - Component props.
 * @param {boolean} iconProps.isSelected - Whether the card is selected.
 * @param {boolean} iconProps.isReadOnly - Whether the card is in read-only mode.
 * @param {boolean} iconProps.isPackage - Whether the card represents a package.
 * @param {function} iconProps.onClick - Click event handler for the card content.
 * @param {object} iconProps.product - Product information.
 * @returns {JSX.Element} - React JSX element representing the product card.
 */
export const ProductCard = (props: any) => {
	const textRef = useRef<HTMLSpanElement | null>(null);
	const [isTruncated, setIsTruncated] = useState(false);

	useEffect(() => {
		if (!textRef.current) return;

		const element = textRef.current;

		const check = () => {
			setIsTruncated(
				element.scrollHeight - element.clientHeight > 1 ||
					element.scrollWidth - element.clientWidth > 1
			);
		};

		check();

		const resizeObserver = new ResizeObserver(check);
		resizeObserver.observe(element);
		return () => resizeObserver.disconnect();
	}, [props.product.name]);

	const backgroundColour = {
		hover: props.isSelected ? colours.green.hover : colours.primary.hover,
		main: props.isSelected ? colours.green.main : colours.primary.main,
		disabled: props.isSelected ? colours.green.hover : `rgba(80, 80, 80, 0.25)`,
	};

	return (
		<Tooltip title={isTruncated ? props.product.name : ''} disableHoverListener={!isTruncated}>
			<Card
				elevation={props.isSelected ? 4 : 2}
				sx={{
					backgroundColor: props.isReadOnly
						? backgroundColour.disabled
						: backgroundColour.main,
					'&:hover': {
						...(!props.isReadOnly && {
							backgroundColor: backgroundColour.hover,
							boxShadow: 4,
						}),
					},
					color: colours.darkGrey.main,
					height: '100%',
				}}
			>
				<CardContent
					onClick={props.onClick}
					sx={{
						cursor: 'pointer',
						...(props.isReadOnly && {
							pointerEvents: 'auto',
							'&:hover': {
								backgroundColor: 'transparent',
							},
							cursor: 'not-allowed !important',
						}),
						p: 0,
						pb: '0 !important',
						height: '100%',
					}}
					key={`card-content-${props.isPackage ? 'package' : 'product'}-${
						props.product.id
					}`}
				>
					<Grid
						container
						height={'100%'}
						alignItems={'center'}
						sx={{ p: '0.5em' }}
						spacing={2}
					>
						<Grid item xs={4} sm={5}>
							<IconFromProductName productName={props.product.name.toLowerCase()} />
						</Grid>
						<Grid item xs={8} sm={7} alignItems='center'>
							<Typography
								ref={textRef}
								variant='quoteTabHeader'
								fontSize='15px'
								sx={{
									color: 'white',
									display: '-webkit-box',
									WebkitBoxOrient: 'vertical',
									WebkitLineClamp: LINES,
									overflow: 'hidden',
									textOverflow: 'ellipsis',

									lineHeight: 1.3,
									maxHeight: `calc(1.3em * ${LINES})`,
								}}
							>
								{props.product.name}
							</Typography>
						</Grid>
					</Grid>
				</CardContent>
			</Card>
		</Tooltip>
	);
};
